import {
	Divider,
	Popover,
	PopoverControlButton,
	PopoverMenu,
	PopoverMenuItem,
} from '@audio-network/componentlibrary'
import { Ellipsis } from '@audio-network/componentlibrary/icons'
import { useTranslation } from 'next-i18next'
import { useId } from 'react'

import AddToProjectsModal from '@/components/shared/AddToProjectsModal/AddToProjectsModal'
import NotePopover from '@/components/shared/TrackActions/NotePopover/NotePopover'
import { useTrackActions } from '@/components/shared/TrackActions/TrackActions.hooks'

import styles from './MoreOptions.module.css'
import { MoreOptionsProps } from './MoreOptions.types'

const MoreOptions = ({ track, source }: MoreOptionsProps): JSX.Element => {
	const { t } = useTranslation(['trackrow'])
	const { t: appTranslation } = useTranslation(['app'])
	const { userActions, trackOptions, projectActions } = useTrackActions({
		track,
		source,
	})

	const projectNoteId = `more-options-note-${track.id}`
	const addToProjectModalId = `more-options-add-to-project-${track.id}`

	const popoverControlButtonId = useId()
	const popoverId = useId()

	return (
		<>
			<PopoverControlButton
				data-testid="more-options-button"
				popovertarget={popoverId}
				id={popoverControlButtonId}
			>
				<Ellipsis title={t('track-actions.more-options')} />
			</PopoverControlButton>
			<Popover
				id={popoverId}
				mobileCloseLabel={appTranslation('close')}
				className={styles.moreOptionsPopover}
			>
				<PopoverMenu>
					{userActions.map((option, index) => {
						if (option.id === 'Projects') {
							return (
								<PopoverMenuItem
									key={`${track.id}-user-${option.id ?? index}`}
									content={
										<button
											type="button"
											className={styles.customOption}
											onClick={option.onClick ?? null}
											{...{
												command: 'show-modal',
												commandfor: addToProjectModalId,
											}}
										>
											{option.Icon}
											{option.title}
										</button>
									}
								/>
							)
						}
						return (
							<PopoverMenuItem
								key={`${track.id}-user-${option.id ?? index}`}
								content={option.title ?? ''}
								href={option.href ?? null}
								startIcon={option.Icon}
								aria-pressed={option['aria-pressed'] ?? null}
								onClick={option.onClick ?? null}
							/>
						)
					})}
					<Divider className={styles.divider} />
					{trackOptions.map((option, index) => (
						<PopoverMenuItem
							key={`${track.id}-track-${option.id ?? index}-${index}`}
							content={option.title ?? ''}
							href={option.href ?? null}
							startIcon={option.Icon}
							onClick={option.onClick ?? null}
						/>
					))}
					{projectActions.length > 0 && (
						<>
							<Divider className={styles.divider} />
							{projectActions.map((option, index) => {
								if (option.id === 'Notes') {
									return (
										<PopoverMenuItem
											key={`${track.id}-project-${option.id ?? index}`}
											content={
												<PopoverControlButton
													className={styles.customOption}
													popovertarget={projectNoteId}
												>
													{option.Icon}
													{option.title}
												</PopoverControlButton>
											}
										/>
									)
								}
								return (
									<PopoverMenuItem
										key={`${track.id}-project-${option.id ?? index}`}
										content={option.title ?? ''}
										startIcon={option.Icon}
										onClick={option.onClick ?? null}
									/>
								)
							})}
						</>
					)}
				</PopoverMenu>
			</Popover>
			{userActions.some((action) => action.id === 'Projects') && (
				<AddToProjectsModal
					id={addToProjectModalId}
					tracks={[track]}
					source={source}
				/>
			)}
			<NotePopover id={projectNoteId} track={track} />
		</>
	)
}

export default MoreOptions
